import { createContext, useContext, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import RecordingStarted from 'sound/recording_started.mp3';
import { useLoadSectionAudioMutation } from '../../../services/api/upload-vehicle';
import { useDispatch } from 'react-redux';
import { snackShow } from '../../../actions';
import toast from 'react-hot-toast';
import { useIsOnline } from 'hooks';
import { db } from 'db';
import { generateRandomId } from 'utils/crypto';

type Props = {
  name: string;
  children: React.ReactNode;
  autosave?: boolean;
};

type SoundRecordingContext = {
  closeRecordingModal: () => void;
  recordingModalOpened: boolean;
  startRecording: () => Promise<void>;
  finishRecording: () => void;
  startRecordingCountdown: () => void;
  stopCountdown: () => void;
  isRecording: boolean;
  recordingTime: number | null;
  shouldRenderCountdown: boolean;
  removeRecording: () => void;
};

const defaultValue: SoundRecordingContext = {
  closeRecordingModal: () => {},
  recordingModalOpened: false,
  startRecording: () => Promise.resolve(),
  finishRecording: () => {},
  startRecordingCountdown: () => {},
  stopCountdown: () => {},
  isRecording: false,
  recordingTime: null,
  shouldRenderCountdown: false,
  removeRecording: () => {}
};

const SoundRecordingContext = createContext(defaultValue);

export const SoundRecordingProvider = ({ children, name, autosave = true }: Props) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number | null>(null);
  const [shouldRenderCountdown, setShouldRenderCountdown] = useState(false);
  const recordingStartedAtRef = useRef<number | null>(null);
  const [recordingModalOpened, setRecordingModalOpened] = useState(false);

  const closeRecordingModal = () => {
    setRecordingModalOpened(false);
    setRecordingTime(null);
  };

  const audioRef = useRef<HTMLAudioElement>(new Audio());

  const { input } = useField(`${name}.audio_url`, { subscription: { value: true } });
  const { input: fileInput } = useField(`${name}.audio_file`, { subscription: { value: true } });

  const {
    input: { value: sectionId }
  } = useField(`${name}.id`);

  const {
    input: { value: vehicleId }
  } = useField('vehicleId');
  const dispatch = useDispatch();

  const [loadAudio] = useLoadSectionAudioMutation();

  const startRecordingCountdown = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({
          audio: true
        })
        .then(stream => {
          mediaRecorderRef.current = new MediaRecorder(stream);
          setShouldRenderCountdown(true);
        })
        .catch(e => {
          console.log(e);
          dispatch(snackShow({ message: 'Allow browser to record audio to proceed', type: 'error' }));
        });
    } else {
      dispatch(snackShow({ message: 'Audio recording is not supported on your browser', type: 'error' }));
    }
  };

  const startRecording = async () => {
    if (!mediaRecorderRef.current) return;
    setIsRecording(true);
    setRecordingTime(null);

    try {
      if (audioRef.current) {
        audioRef.current.src = RecordingStarted;
        await audioRef.current.play();
      }
    } catch (error) {
      console.error(error, 'error while playing sound');
    }

    mediaRecorderRef.current.onstart = e => {
      recordingStartedAtRef.current = e.timeStamp;
    };

    mediaRecorderRef.current.start(1000);

    const localChunks: Blob[] = [];
    mediaRecorderRef.current.ondataavailable = (e: BlobEvent) => {
      localChunks.push(e.data);
      setRecordingTime(e.timeStamp - recordingStartedAtRef.current!);
      if (localChunks.length >= 25) {
        finishRecording();
      }
    };

    mediaRecorderRef.current.onstop = async () => {
      onAudioStopped(localChunks);
    };
  };

  const finishRecording = () => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
  };

  const isOnline = useIsOnline();

  const onAudioStopped = async (chunks: Blob[]) => {
    if (!mediaRecorderRef.current) return;
    const audioBlob = new Blob(chunks, { type: 'audio/wav' });
    if (chunks.length > 0) {
      if (autosave) {
        if (!isOnline) {
          await db.entityUpdates.add({
            id: generateRandomId(),
            entityId: sectionId,
            entityType: 'section',
            formId: vehicleId,
            timestamp: Date.now(),
            synced: false,
            key: 'audio',
            value: audioBlob
          });
          input.onChange(URL.createObjectURL(audioBlob));
          cleanupRecording();
          return;
        }
        const response = await loadAudio({ vehicleId, audio: audioBlob, sectionId });
        if ('data' in response) {
          input.onChange(response.data.data.url);
          toast.success('Audio saved');
        }
      } else {
        fileInput.onChange(audioBlob);
      }
    }

    cleanupRecording();
  };

  const cleanupRecording = () => {
    setIsRecording(false);
    setRecordingTime(null);
    recordingStartedAtRef.current = null;

    setTimeout(() => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        mediaRecorderRef.current = null;
      }
    }, 500);
  };

  const removeRecording = () => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.onstop = null;
    mediaRecorderRef.current.stop();
    setIsRecording(false);
    setRecordingTime(null);
    recordingStartedAtRef.current = null;
  };

  return (
    <SoundRecordingContext.Provider
      value={{
        startRecording,
        finishRecording,
        startRecordingCountdown,
        isRecording,
        recordingTime,
        shouldRenderCountdown,
        removeRecording,
        stopCountdown: () => setShouldRenderCountdown(false),
        recordingModalOpened,
        closeRecordingModal
      }}
    >
      {children}
    </SoundRecordingContext.Provider>
  );
};

export const useSoundRecordingContext = () => {
  const context = useContext(SoundRecordingContext);
  return context;
};
